/**
 * Theme hook — gestiona el toggle light/dark/system y persiste la
 * preferencia del usuario en localStorage.
 *
 * Resolución:
 *   - `light` / `dark` → fuerza esa clase en <html> y persiste
 *   - `system` → sigue `prefers-color-scheme`, no persiste (default)
 *
 * Para evitar FOUC (flash of wrong theme en la carga inicial), cada app
 * debe incluir un script síncrono en <head> que aplique la clase `.dark`
 * ANTES de que React monte. Ver `themeInitScript` abajo.
 */

import { useEffect, useState } from 'react';

export type Theme = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'atreus.theme';
const MQ = '(prefers-color-scheme: dark)';

const getSystemTheme = (): 'light' | 'dark' =>
  typeof window !== 'undefined' && window.matchMedia(MQ).matches
    ? 'dark'
    : 'light';

const applyTheme = (theme: Theme): void => {
  if (typeof document === 'undefined') return;
  const resolved = theme === 'system' ? getSystemTheme() : theme;
  document.documentElement.classList.toggle('dark', resolved === 'dark');
};

const readStored = (): Theme => {
  if (typeof window === 'undefined') return 'system';
  const raw = window.localStorage.getItem(STORAGE_KEY);
  return raw === 'light' || raw === 'dark' ? raw : 'system';
};

export const useTheme = (): {
  theme: Theme;
  resolved: 'light' | 'dark';
  setTheme: (t: Theme) => void;
} => {
  const [theme, setThemeState] = useState<Theme>(readStored);

  useEffect(() => {
    applyTheme(theme);
    if (theme === 'system') {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, theme);
    }
  }, [theme]);

  // Seguir cambios del OS cuando el user eligió `system`
  useEffect(() => {
    if (theme !== 'system') return;
    const mq = window.matchMedia(MQ);
    const handler = (): void => applyTheme('system');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  // Sincronizar entre pestañas
  useEffect(() => {
    const handler = (e: StorageEvent): void => {
      if (e.key !== STORAGE_KEY) return;
      setThemeState(readStored());
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const resolved: 'light' | 'dark' =
    theme === 'system' ? getSystemTheme() : theme;

  return { theme, resolved, setTheme: setThemeState };
};

/**
 * Llamar al inicio de `main.tsx` (antes de `createRoot().render()`) para
 * aplicar la clase `.dark` antes de la primera pintura de React y evitar
 * el flash de tema incorrecto en la carga inicial.
 */
export const initTheme = (): void => {
  try {
    applyTheme(readStored());
  } catch {
    /* ignore — el user no tiene localStorage (modo privado strict) */
  }
};
